// client/src/components/ImageGenDesign.js
import React from 'react';

function ImageGenDesign(props) {
    // Destructure all props for cleaner code
    const {
        manualPrompt, setManualPrompt, imageUrl, loading, error, clients, selectedClientId,
        handleClientSelectChange, handleManualPromptChange, handleSingleGenerate,
        displayedPrompt, handleSingleDownload, generatedImageRecords, handleDownloadGeneratedExcel,
        excelFile, handleExcelFileChange, fileInputRef, bulkSelectedSizePreset, handleBulkSizePresetChange,
        bulkOutputFormat, handleBulkOutputFormatChange, handleExcelUpload, excelLoading, excelError,
        excelResults, handleBatchDownload, handleBulkRegenerate, handleBulkDelete,
        logoFile, handleLogoFileChange, handleUploadLogo, logoFileInputRef, loadingLogoUpload,
        logoCrudError, allLogos, selectedLogoId, handleLogoSelectChange, selectedLogoUrl,
        handleDeleteLogo,
        logoCombineExcelFile, handleLogoCombineExcelChange, logoCombineFileInputRef,
        handleProcessImagesWithSelectedLogo, logoCombineLoading, logoCombineError,
        logoCombineResults, handleDownloadLogoCombineExcel,
        selectedSizePreset, handleSizePresetChange, outputFormat, handleFormatChange,
        availableSizePresets, availableFormats,
        displayMode, setDisplayMode,
        isSingleGenDisabled, isBulkGenDisabled, isLogoCombinerDisabled,
        BACKEND_URL,setError,originalCombineExcelData
    } = props;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-700 text-white font-inter flex flex-col items-center justify-start p-4 sm:p-6 lg:p-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-center text-indigo-300 mb-6 drop-shadow-lg">
                Digital Poster Generator
            </h1>

            {/* NEW: Toggle Buttons for Display Mode */}
            <div className="flex justify-center gap-4 mb-8 w-full max-w-md">
                <button
                    onClick={() => setDisplayMode('poster')}
                    className={`py-3 px-6 rounded-lg font-bold text-lg transition-all duration-300 ease-in-out
                                ${displayMode === 'poster'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                        }`}
                >
                    Poster Generator
                </button>

            </div>


            {/* Combined Error Message & Loading Indicator (at the top, below title) */}
            {(error || excelError || logoCrudError || logoCombineError) && (
                <p className="text-red-400 text-center mt-4 text-sm sm:text-base p-2 bg-red-900 bg-opacity-30 rounded-md w-full max-w-2xl">
                    {error || excelError || logoCrudError || logoCombineError}
                </p>
            )}
            {(loading || excelLoading || loadingLogoUpload || logoCombineLoading) && (
                <div className="flex justify-center items-center mt-6">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-500"></div>
                    <p className="ml-4 text-indigo-300 text-lg">
                        {loading ? 'Generating single poster...' :
                            excelLoading ? 'Generating bulk posters, please wait...' :
                                loadingLogoUpload ? 'Processing logo...' :
                                    'Combining images with logos...'}
                    </p>
                </div>
            )}

            {/* Main content area: Image display on left, prompt/download on right */}
            <div className="w-full max-w-6xl bg-gray-800 p-6 rounded-xl shadow-2xl mt-8 flex flex-col md:flex-row items-center md:items-start justify-center gap-8">
                {/* Image Display Area (Left Side) - This is already conditionally rendered correctly within this parent div */}
                <div className="flex-1 flex flex-col items-center justify-start min-w-[300px]">
                    {imageUrl && !excelLoading && excelResults.length === 0 && logoCombineResults.length === 0 ? (
                        <div className="image-display-area bg-gray-700 p-4 rounded-xl shadow-inner flex flex-col items-center w-full h-[400px] border-2 border-indigo-500">
                            <h2 className="text-xl sm:text-2xl font-semibold text-indigo-300 mb-3">Generated AI Poster:</h2>
                            <div className="flex-grow flex items-center justify-center w-full">
                                <img
                                    src={imageUrl}
                                    alt="Generated AI Poster"
                                    className="max-w-full max-h-full object-contain rounded-lg shadow-lg border-2 border-indigo-500 transform transition-transform duration-300 hover:scale-105"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://placehold.co/400x400/FF0000/FFFFFF?text=Poster+Load+Error";
                                        setError("Poster image failed to load. The generated image URL might be invalid.");
                                    }}
                                />
                            </div>
                        </div>
                    ) : excelResults.length > 0 && !loading && !logoCombineLoading ? (
                        <div className="bg-gray-700 p-4 rounded-xl shadow-inner w-full h-[400px] border-2 border-teal-500 flex flex-col">
                            <h2 className="text-xl sm:text-2xl font-semibold text-teal-300 mb-3 text-center">Bulk Generation Results:</h2>
                            <div className="flex-grow max-h-full overflow-y-auto custom-scrollbar pr-2">
                                {excelResults.map((result, index) => (
                                    <div key={index} className={`p-3 mb-2 rounded-lg ${result.success ? 'bg-green-800 bg-opacity-30' : 'bg-red-800 bg-opacity-30'}`}>
                                        {/* Display result.filename instead of result.prompt */}
                                        <p className="font-semibold text-lg">{result.filename || result.prompt}</p>
                                        {result.success ? (
                                            <p className="text-green-300 text-sm">Success! <a href={`${BACKEND_URL}${result.imageUrl}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-200">View Image</a></p>
                                        ) : (
                                            <p className="text-red-300 text-sm">Failed: {result.error}</p>
                                        )}
                                        {/* --- NEW: Regenerate & Delete buttons --- */}
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => handleBulkRegenerate(result, index)}
                                                disabled={excelLoading}
                                                className="py-1 px-3 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                Regenerate
                                            </button>
                                            <button
                                                onClick={() => handleBulkDelete(result, index)}
                                                disabled={excelLoading}
                                                className="py-1 px-3 rounded bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={handleBatchDownload}
                                disabled={excelLoading}
                                className="mt-4 w-full py-3 px-6 rounded-lg font-bold text-lg text-white
                                bg-gradient-to-r from-emerald-600 to-green-600
                                hover:from-emerald-700 hover:to-green-700
                                focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800
                                transition-all duration-300 ease-in-out
                                shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Download All Generated Posters
                            </button>
                        </div>
                    ) : logoCombineResults.length > 0 && !logoCombineLoading && !loading && !excelLoading ? (
                        <div className="bg-gray-700 p-4 rounded-xl shadow-inner w-full h-[400px] border-2 border-purple-500 flex flex-col">
                            <h2 className="text-xl sm:text-2xl font-semibold text-purple-300 mb-3 text-center">Logo Combined Results:</h2>
                            <div className="flex-grow max-h-full overflow-y-auto custom-scrollbar pr-2">
                                {logoCombineResults.map((result, index) => (
                                    <div key={index} className={`p-3 mb-2 rounded-lg ${result.success ? 'bg-indigo-800 bg-opacity-30' : 'bg-red-800 bg-opacity-30'}`}>
                                        <p className="font-semibold text-lg">Original: {result.originalFilename}</p>
                                        {result.success ? (
                                            <>
                                                <p className="text-indigo-300 text-sm">Processed: {result.processedFilename}</p>
                                                <a href={`${BACKEND_URL}${result.processedImageUrl}`} target="_blank" rel="noopener noreferrer" className="underline text-indigo-200 hover:text-indigo-100 text-sm">View/Download Processed Image</a>
                                            </>
                                        ) : (
                                            <p className="text-red-300 text-sm">Failed: {result.error}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={handleDownloadLogoCombineExcel}
                                disabled={logoCombineLoading || originalCombineExcelData.length === 0}
                                className="mt-4 w-full py-3 px-6 rounded-lg font-bold text-lg text-white
                                bg-gradient-to-r from-purple-600 to-blue-600
                                hover:from-purple-700 hover:to-blue-700
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800
                                transition-all duration-300 ease-in-out
                                shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Download Updated Excel Report
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center bg-gray-700 p-6 rounded-xl shadow-inner w-full h-[400px] text-gray-400 text-center text-lg italic border-2 border-dashed border-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mb-4 text-indigo-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                            </svg>
                            Your generated poster will appear here, or bulk results will be shown.
                            <p className="mt-2 text-sm">Use the options below to generate.</p>
                        </div>
                    )}
                </div>

                {/* Prompt and Download Button (Right Side) */}
                <div className="flex-1 flex flex-col items-center justify-center p-4 bg-gray-700 rounded-xl shadow-inner border-2 border-indigo-500 min-h-[400px]">
                    {displayedPrompt && (
                        <p className="text-gray-300 text-center text-sm sm:text-base italic break-words w-full px-2 mb-4">
                            Prompt Used: "<span className="font-medium text-indigo-200">{displayedPrompt}</span>"
                        </p>
                    )}
                    <button
                        onClick={handleSingleDownload}
                        disabled={!imageUrl || loading || excelLoading || logoCombineLoading} // Disable if no image or other process active
                        className="w-full py-3 px-6 rounded-lg font-bold text-lg text-white
                        bg-gradient-to-r from-emerald-600 to-green-600
                        hover:from-emerald-700 hover:to-green-700
                        focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800
                        transition-all duration-300 ease-in-out
                        shadow-lg hover:shadow-xl
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Download Poster
                    </button>
                    {/* NEW: Download Excel Button - Conditional for single generation ONLY */}
                    {generatedImageRecords.length > 0 && imageUrl && excelResults.length === 0 && logoCombineResults.length === 0 && (
                        <button
                            onClick={handleDownloadGeneratedExcel}
                            className="mt-4 w-full py-3 px-6 rounded-lg font-bold text-lg text-white
                            bg-gradient-to-r from-blue-600 to-purple-600
                            hover:from-blue-700 hover:to-purple-700
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800
                            transition-all duration-300 ease-in-out
                            shadow-lg hover:shadow-xl"
                        >
                            Download Generated Image Report (Excel)
                        </button>
                    )}
                </div>
            </div>

            {/* All input sections arranged horizontally below the image/prompt area */}
            <div className="flex flex-col lg:flex-row lg:justify-center lg:items-start gap-8 w-full max-w-6xl mt-8">
                {/* --- Conditional Display for Poster Generator Sections --- */}
                {displayMode === 'poster' && (
                    <>
                        {/* Single Poster Generation Column */}
                        <div className="bg-gray-800 p-6 rounded-xl shadow-2xl flex-1 min-w-[300px]">
                            <h2 className="text-xl sm:text-2xl font-semibold text-indigo-300 mb-4">Single Poster Generation</h2>

                            {/* Client Selection Dropdown */}
                            <div>
                                <label htmlFor="client-select" className="block text-lg font-medium text-gray-300 mb-2">
                                    Select a Client (for poster generation):
                                </label>
                                <select
                                    id="client-select"
                                    value={selectedClientId}
                                    onChange={handleClientSelectChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg cursor-pointer"
                                    disabled={isSingleGenDisabled}
                                >
                                    <option value="">-- Select Client --</option>
                                    {clients.length === 0 && !loading ? (
                                        <option disabled>Loading clients...</option>
                                    ) : (
                                        clients.map((client) => (
                                            <option key={client._id} value={client._id}>
                                                {client.clientName}
                                            </option>
                                        ))
                                    )}
                                </select>
                                {clients.length === 0 && !loading && <p className="text-gray-400 text-sm mt-2">No clients available. Add clients via your CRM.</p>}
                            </div>

                            <div className="text-center text-gray-400 my-4 text-lg font-bold">- OR -</div>

                            {/* Manual Prompt Input */}
                            <div>
                                <label htmlFor="manualPrompt" className="block text-lg font-medium text-gray-300 mb-2">
                                    Enter Manual Prompt (to generate a custom poster):
                                </label>
                                <textarea
                                    id="manualPrompt"
                                    value={manualPrompt}
                                    onChange={handleManualPromptChange}
                                    placeholder="e.g., A futuristic city at sunset, highly detailed, cyberpunk style"
                                    rows="3"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg"
                                       disabled={isSingleGenDisabled || selectedClientId}
                                ></textarea>
                            </div>

                            {/* Image Size Preset (Single Gen) */}
                            <div>
                                <label htmlFor="image-size-preset" className="block text-lg font-medium text-gray-300 mb-2">
                                    Image Size Preset:
                                </label>
                                <select
                                    id="image-size-preset"
                                    value={selectedSizePreset}
                                    onChange={handleSizePresetChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg cursor-pointer"
                                    disabled={isSingleGenDisabled}
                                >
                                    {availableSizePresets.map(preset => (
                                        <option key={preset.value} value={preset.value}>
                                            {preset.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Output Format (Single Gen) */}
                            <div>
                                <label htmlFor="output-format" className="block text-lg font-medium text-gray-300 mb-2">
                                    Output Format:
                                </label>
                                <select
                                    id="output-format"
                                    value={outputFormat}
                                    onChange={handleFormatChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg cursor-pointer"
                                    disabled={isSingleGenDisabled}
                                >
                                    {availableFormats.map(format => (
                                        <option key={format.value} value={format.value}>
                                            {format.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleSingleGenerate}
                                disabled={isSingleGenDisabled || (!selectedClientId && !manualPrompt.trim())}
                                className="mt-6 w-full py-3 px-6 rounded-lg font-bold text-lg text-white
                                bg-gradient-to-r from-blue-600 to-indigo-600
                                hover:from-blue-700 hover:to-indigo-700
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800
                                transition-all duration-300 ease-in-out
                                shadow-lg hover:shadow-xl
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-500 disabled:to-gray-600"
                            >
                                {loading ? 'Generating...' : 'Generate Single Poster'}
                            </button>
                        </div>

                        {/* Bulk Poster Generation Column (AI) */}
                        <div className="bg-gray-800 p-6 rounded-xl shadow-2xl flex-1 min-w-[300px]">
                            <a
                                href="/sample-excel-single.xlsx"
                                download="sample-excel-single.xlsx"
                                style={{ color: 'white', textDecoration: 'underline' }}
                            >
                                Download Sample Excel Sheet
                            </a>
                            <h2 className="text-xl sm:text-2xl font-semibold text-teal-300 mb-4">Bulk AI Poster Generation (via Excel)</h2>

                            {/* Excel File Input */}
                            <div>
                                <label htmlFor="excelFileInput" className="block text-lg font-medium text-gray-300 mb-2">
                                    upload blog excelsheet and its is must need to have featureImage column
                                    because image generator take it as prompt for image generating and it will take topic column value for file name and save it on backend:
                                </label>
                                <input
                                    type="file"
                                    id="excelFileInput"
                                    accept=".xlsx, .xls"
                                    onChange={handleExcelFileChange}
                                    ref={fileInputRef}
                                    className="w-full text-white text-base file:mr-4 file:py-2 file:px-4
                                    file:rounded-full file:border-0 file:text-sm file:font-semibold
                                    file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100
                                    disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isBulkGenDisabled}
                                />
                                {excelFile && <p className="text-gray-400 text-sm mt-2">Selected Excel file: {excelFile.name}</p>}
                            </div>

                            {/* NEW: Image Size Preset (Bulk Gen) */}
                            <div className="mt-4">
                                <label htmlFor="bulk-image-size-preset" className="block text-lg font-medium text-gray-300 mb-2">
                                    Image Size Preset (Bulk):
                                </label>
                                <select
                                    id="bulk-image-size-preset"
                                    value={bulkSelectedSizePreset}
                                    onChange={handleBulkSizePresetChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base sm:text-lg cursor-pointer"
                                    disabled={isBulkGenDisabled}
                                >
                                    {availableSizePresets.map(preset => (
                                        <option key={preset.value} value={preset.value}>
                                            {preset.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* NEW: Output Format (Bulk Gen) */}
                            <div className="mt-4">
                                <label htmlFor="bulk-output-format" className="block text-lg font-medium text-gray-300 mb-2">
                                    Output Format (Bulk):
                                </label>
                                <select
                                    id="bulk-output-format"
                                    value={bulkOutputFormat}
                                    onChange={handleBulkOutputFormatChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base sm:text-lg cursor-pointer"
                                    disabled={isBulkGenDisabled}
                                >
                                    {availableFormats.map(format => (
                                        <option key={format.value} value={format.value}>
                                            {format.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleExcelUpload}
                                disabled={isBulkGenDisabled || !excelFile}
                                className="mt-3 w-full py-2 px-4 rounded-lg font-bold text-base text-white
                                bg-gradient-to-r from-teal-600 to-green-600
                                hover:from-teal-700 hover:to-green-700
                                focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800
                                transition-all duration-300 ease-in-out
                                shadow-md hover:shadow-lg
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-500 disabled:to-gray-600"
                            >
                                {excelLoading ? 'Processing Excel...' : 'Generate Posters from Excel'}
                            </button>
                        </div>
                    </>
                )}

            </div>
        </div>
    );
}

export default ImageGenDesign;